import React, { useEffect, useState } from 'react';
import {
  Table,
  Tag,
  Button,
  Modal,
  Select,
  Input,
  Form,
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Typography,
  Divider,
  Result,
  message as antMessage,
} from 'antd';
import {
  CustomerServiceOutlined,
  ReloadOutlined,
  SearchOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ShopOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  QuestionCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import MainLayout from '../../components/MainLayout';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { useSelector } from 'react-redux';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const statusColorMap: Record<string, { color: string; icon: React.ReactNode }> = {
  OPEN: { color: 'error', icon: <ClockCircleOutlined /> },
  IN_PROGRESS: { color: 'processing', icon: <SyncOutlined spin /> },
  RESOLVED: { color: 'success', icon: <CheckCircleOutlined /> },
  CLOSED: { color: 'default', icon: <CloseCircleOutlined /> },
};

const SupportTickets: React.FC = () => {
  const userDetail = useSelector((state: any) => state.UserData?.userData);
  const isSuperAdmin = userDetail?.role === 'superAdmin';
  const isFranchise = userDetail?.role === 'franchise';
  const isAdmin = isSuperAdmin || userDetail?.role === 'admin' || userDetail?.user?.role === 'store_owner' || userDetail?.user?.role === 'admin';

  // Support Contact Settings
  const [contactPhone, setContactPhone] = useState<string>('');
  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactLoading, setContactLoading] = useState<boolean>(false);
  const [contactSaving, setContactSaving] = useState<boolean>(false);
  const [contactStoreId, setContactStoreId] = useState<string>('ALL');
  const [contactForm] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [tickets, setTickets] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>('ALL');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [searchText, setSearchText] = useState<string>('');

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [form] = Form.useForm();

  const [guestSubmitted, setGuestSubmitted] = useState<boolean>(false);
  const [guestLoading, setGuestLoading] = useState<boolean>(false);
  const [guestForm] = Form.useForm();
  const isAuthenticated = !!userDetail?.role;

  // Fetch Stores for SuperAdmin filter
  const fetchStores = async () => {
    if (!isAuthenticated || !isSuperAdmin) return;
    try {
      const res = await ApiServices('GET', ApiConfig.STORES);
      if (res?.data) {
        setStores(res.data);
      }
    } catch (e) {
      console.error('Error fetching stores:', e);
    }
  };

  // Fetch Tickets from API (Authenticated only)
  const fetchTickets = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', String(page));
      queryParams.append('limit', String(limit));
      if (selectedStatus && selectedStatus !== 'ALL') {
        queryParams.append('status', selectedStatus);
      }
      if (isSuperAdmin && selectedStoreId && selectedStoreId !== 'ALL') {
        queryParams.append('storeId', selectedStoreId);
      }
      if (searchText) {
        queryParams.append('search', searchText);
      }

      const url = `${ApiConfig.SUPPORT_TICKETS}?${queryParams.toString()}`;
      const res = await ApiServices('GET', url);

      if (res?.success) {
        setTickets(res.data || []);
        setTotalCount(res.totalCount || 0);
      } else {
        antMessage.error(res?.message || 'Failed to load support tickets');
      }
    } catch (err: any) {
      console.error('Error fetching support tickets:', err);
      antMessage.error('Error connecting to support service');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestSubmit = async (values: any) => {
    setGuestLoading(true);
    try {
      const res = await ApiServices('POST', ApiConfig.SUPPORT_CREATE_TICKET, {
        name: values.name,
        email: values.email,
        subject: values.subject,
        message: values.message,
      });

      if (res?.success) {
        setGuestSubmitted(true);
        guestForm.resetFields();
        antMessage.success('Support inquiry submitted successfully!');
      } else {
        antMessage.error(res?.message || 'Failed to submit inquiry');
      }
    } catch (error: any) {
      antMessage.error(error?.message || 'Failed to connect to support service. Please try again.');
    } finally {
      setGuestLoading(false);
    }
  };

  const fetchSupportContact = async (storeIdOverride?: string) => {
    if (!isAuthenticated) return;
    setContactLoading(true);
    try {
      const targetStore = storeIdOverride !== undefined
        ? storeIdOverride
        : (isSuperAdmin ? contactStoreId : (userDetail?.storeId || userDetail?.user?.storeId || ''));
      const query = targetStore && targetStore !== 'ALL' ? `?storeId=${targetStore}` : '';
      const res = await ApiServices('GET', `${ApiConfig.SUPPORT_CONTACT}${query}`);
      if (res?.data) {
        const phone = res.data.phone || '';
        const email = res.data.email || '';
        setContactPhone(phone);
        setContactEmail(email);
        contactForm.setFieldsValue({
          phone,
          email,
        });
      }
    } catch (e) {
      console.error('Error fetching support contact:', e);
    } finally {
      setContactLoading(false);
    }
  };

  const handleSaveContact = async (values: { phone: string; email: string }) => {
    setContactSaving(true);
    try {
      const payload: any = {
        phone: values.phone?.trim() || '',
        email: values.email?.trim() || '',
      };
      if (isSuperAdmin && contactStoreId && contactStoreId !== 'ALL') {
        payload.storeId = contactStoreId;
      }
      const res = await ApiServices('POST', ApiConfig.SUPPORT_CONTACT, payload);
      if (res?.success) {
        antMessage.success(res.message || 'Support contact details updated successfully');
        setContactPhone(payload.phone);
        setContactEmail(payload.email);
      } else {
        antMessage.error(res?.message || 'Failed to update support contact details');
      }
    } catch (e: any) {
      console.error('Error saving support contact:', e);
      antMessage.error('Error connecting to support service');
    } finally {
      setContactSaving(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSupportContact();
    }
  }, [isAuthenticated, contactStoreId]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStores();
    }
  }, [isSuperAdmin, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTickets();
    }
  }, [page, limit, selectedStoreId, selectedStatus, isAuthenticated]);

  const handleSearch = () => {
    setPage(1);
    fetchTickets();
  };

  const handleResetFilters = () => {
    setSelectedStoreId('ALL');
    setSelectedStatus('ALL');
    setSearchText('');
    setPage(1);
  };

  const openManageModal = (ticket: any) => {
    setSelectedTicket(ticket);
    form.setFieldsValue({
      status: ticket.status,
      reply: ticket.reply || '',
    });
    setIsModalOpen(true);
  };

  const handleSaveTicket = async (values: any) => {
    if (!selectedTicket) return;
    setSubmitting(true);
    try {
      const url = `${ApiConfig.SUPPORT_TICKETS}/${selectedTicket.id}`;
      const res = await ApiServices('PUT', url, {
        status: values.status,
        reply: values.reply,
      });

      if (res?.success) {
        antMessage.success('Support ticket updated successfully');
        setIsModalOpen(false);
        fetchTickets();
      } else {
        antMessage.error(res?.message || 'Failed to update support ticket');
      }
    } catch (err: any) {
      console.error('Error updating ticket:', err);
      antMessage.error('Failed to update support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  // Stats calculation
  const openCount = tickets.filter((t) => t.status === 'OPEN').length;
  const inProgressCount = tickets.filter((t) => t.status === 'IN_PROGRESS').length;
  const resolvedCount = tickets.filter((t) => t.status === 'RESOLVED').length;

  const columns = [
    {
      title: 'Ticket ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id: number) => <Text style={{ fontWeight: 'bold', color: '#1890ff' }}>#ST-{id}</Text>,
    },
    {
      title: 'Submitter / Driver',
      key: 'submitter',
      render: (record: any) => (
        <Space direction="vertical" size={2}>
          <Space>
            <UserOutlined style={{ color: '#4b5563' }} />
            <Text strong>{record.name || 'Anonymous'}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <MailOutlined style={{ marginRight: 4 }} />
            {record.email}
          </Text>
          {record.driver && (
            <Tag color="blue" style={{ marginTop: 2 }}>
              Driver: {record.driver.firstName} {record.driver.lastName}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Store',
      key: 'store',
      render: (record: any) => {
        const storeName = record.store?.name || 'Default Store';
        return (
          <Space>
            <ShopOutlined style={{ color: '#0700d6' }} />
            <Text>{storeName}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Subject & Message',
      key: 'subject',
      render: (record: any) => (
        <div style={{ maxWidth: 320 }}>
          <Text strong style={{ display: 'block', marginBottom: 2 }}>
            {record.subject || 'Support Request'}
          </Text>
          <Paragraph
            ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
            style={{ color: '#6b7280', fontSize: 12, marginBottom: 0 }}
          >
            {record.message}
          </Paragraph>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => {
        const cfg = statusColorMap[status] || { color: 'default', icon: null };
        return (
          <Tag color={cfg.color} icon={cfg.icon} style={{ borderRadius: 6, padding: '2px 8px' }}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Submitted At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => (date ? moment(date).format('DD MMM YYYY, hh:mm A') : '—'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      render: (record: any) => (
        <Button
          type="primary"
          ghost
          icon={<MessageOutlined />}
          onClick={() => openManageModal(record)}
          size="small"
        >
          Manage
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: 24, maxWidth: isAuthenticated ? '100%' : 840, margin: '0 auto', width: '100%' }}>
        {/* If guest user, show clean Contact Support inquiry form */}
        {!isAuthenticated ? (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 26,
                  marginBottom: 16,
                  boxShadow: '0 8px 20px rgba(37, 99, 235, 0.3)'
                }}
              >
                <CustomerServiceOutlined />
              </div>
              <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
                Contact & Support
              </Title>
              <Text type="secondary" style={{ fontSize: 14, display: 'block', marginTop: 6 }}>
                Have questions or need assistance? Reach out to our delivery operations team.
              </Text>
            </div>

            {guestSubmitted ? (
              <Card style={{ borderRadius: 16, boxShadow: '0 4px 20px rgba(15, 23, 42, 0.05)', textAlign: 'center' }}>
                <Result
                  status="success"
                  title="Your Message Has Been Submitted!"
                  subTitle="Thank you for getting in touch. Our operations team has received your ticket and will follow up with you via email shortly."
                  extra={[
                    <Button
                      type="primary"
                      key="another"
                      onClick={() => setGuestSubmitted(false)}
                      size="large"
                    >
                      Send Another Message
                    </Button>
                  ]}
                />
              </Card>
            ) : (
              <Card
                style={{
                  borderRadius: 16,
                  boxShadow: '0 4px 20px rgba(15, 23, 42, 0.05)',
                  border: '1px solid #e2e8f0'
                }}
              >
                <Form
                  form={guestForm}
                  layout="vertical"
                  onFinish={handleGuestSubmit}
                  size="large"
                >
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: 600 }}>Your Name</span>}
                        name="name"
                        rules={[{ required: true, message: 'Please enter your name' }]}
                      >
                        <Input prefix={<UserOutlined style={{ color: '#94a3b8' }} />} placeholder="e.g. Rahul Patel" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: 600 }}>Email Address</span>}
                        name="email"
                        rules={[
                          { required: true, message: 'Please enter your email' },
                          { type: 'email', message: 'Please enter a valid email address' }
                        ]}
                      >
                        <Input prefix={<MailOutlined style={{ color: '#94a3b8' }} />} placeholder="name@company.com" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label={<span style={{ fontWeight: 600 }}>Subject</span>}
                    name="subject"
                    rules={[{ required: true, message: 'Please enter subject' }]}
                  >
                    <Input prefix={<QuestionCircleOutlined style={{ color: '#94a3b8' }} />} placeholder="What can we help you with?" />
                  </Form.Item>

                  <Form.Item
                    label={<span style={{ fontWeight: 600 }}>Your Message</span>}
                    name="message"
                    rules={[{ required: true, message: 'Please enter your message' }]}
                  >
                    <TextArea rows={5} placeholder="Describe your question, integration inquiry, or issue in detail..." />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    loading={guestLoading}
                    icon={<SendOutlined />}
                    style={{
                      height: 48,
                      borderRadius: 10,
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                      marginTop: 8
                    }}
                  >
                    Submit Support Inquiry
                  </Button>
                </Form>
              </Card>
            )}
          </div>
        ) : (
          <div>
            {/* Header Title */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <Title level={3} style={{ margin: 0, color: '#111827' }}>
                  <CustomerServiceOutlined style={{ marginRight: 10, color: '#2563eb' }} />
                  Contact & Support Management
                </Title>
                <Text type="secondary">Manage driver inquiries, support tickets, and franchise support contact info</Text>
              </div>
              <Button icon={<ReloadOutlined />} onClick={() => { fetchTickets(); fetchSupportContact(); }} loading={loading || contactLoading}>
                Refresh
              </Button>
            </div>

            {/* ── Support Contact Details Card (Admin Configuration) ──────────── */}
            {isAdmin && (
              <Card
                style={{
                  marginBottom: 24,
                  borderRadius: 14,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  background: '#ffffff',
                }}
                bodyStyle={{ padding: '20px 24px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ flex: '1 1 300px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Text strong style={{ fontSize: 16, color: '#0f172a' }}>
                        <PhoneOutlined style={{ color: '#2563eb', marginRight: 6 }} />
                        Franchise & Driver Support Contact Details
                      </Text>
                      <Tag color="blue" style={{ borderRadius: 6, fontWeight: 500, margin: 0 }}>
                        Shown in Franchise Footer
                      </Tag>
                    </div>
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                      Set the official support phone number and support email shown in the footer on the Franchise dashboard and throughout the platform.
                    </Text>
                  </div>

                  {isSuperAdmin && stores.length > 0 && (
                    <div style={{ flex: '0 1 260px', minWidth: 200 }}>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4, fontWeight: 500 }}>
                        Configure For Store:
                      </Text>
                      <Select
                        style={{ width: '100%' }}
                        value={contactStoreId}
                        onChange={(val) => setContactStoreId(val)}
                      >
                        <Option value="ALL">Global Default (All Stores)</Option>
                        {stores.map((s) => (
                          <Option key={s.id} value={String(s.id)}>
                            {s.name} ({s.shop || s.shopUrl})
                          </Option>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>

                <Form
                  form={contactForm}
                  layout="vertical"
                  onFinish={handleSaveContact}
                  initialValues={{ phone: contactPhone, email: contactEmail }}
                >
                  <Row gutter={[16, 16]} align="bottom">
                    <Col xs={24} sm={24} md={10} lg={10}>
                      <Form.Item
                        label={<span style={{ fontWeight: 600, color: '#334155' }}>Support Phone Number</span>}
                        name="phone"
                        rules={[
                          { required: true, message: 'Please enter support phone number' },
                          {
                            pattern: /^[+0-9\s\-()]{7,20}$/,
                            message: 'Please enter a valid phone number (e.g. +91 79848 02905)',
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          size="large"
                          placeholder="e.g. +91 79848 02905"
                          prefix={<PhoneOutlined style={{ color: '#2563eb' }} />}
                          allowClear
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={10} lg={10}>
                      <Form.Item
                        label={<span style={{ fontWeight: 600, color: '#334155' }}>Support Email</span>}
                        name="email"
                        rules={[
                          { required: true, message: 'Please enter support email address' },
                          { type: 'email', message: 'Please enter a valid email address' },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          size="large"
                          placeholder="e.g. support@deliveryoperations.com"
                          prefix={<MailOutlined style={{ color: '#2563eb' }} />}
                          allowClear
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={4} lg={4}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        icon={<SaveOutlined />}
                        loading={contactSaving || contactLoading}
                        style={{
                          width: '100%',
                          borderRadius: 8,
                          background: '#2563eb',
                          fontWeight: 600,
                          height: 40,
                        }}
                      >
                        Save
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card>
            )}

            {/* ── Franchise Support Contact Info Banner ───────────────────────── */}
            {isFranchise && (
              <Card
                style={{
                  marginBottom: 24,
                  borderRadius: 14,
                  border: '1px solid #bfdbfe',
                  background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                }}
                bodyStyle={{ padding: '16px 20px' }}
              >
                <Row justify="space-between" align="middle" gutter={[16, 12]}>
                  <Col xs={24} md={14}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <CustomerServiceOutlined style={{ fontSize: 24, color: '#2563eb' }} />
                      <div>
                        <Text strong style={{ fontSize: 15, color: '#1e3a8a' }}>
                          Need Support with Delivery Operations?
                        </Text>
                        <Text type="secondary" style={{ display: 'block', fontSize: 13, color: '#3b82f6' }}>
                          Contact your operations support team directly via phone or email for assistance with orders, drivers, or route management.
                        </Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} md={10} style={{ textAlign: 'right' }}>
                    <Space wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
                      {contactPhone && (
                        <Button
                          type="primary"
                          icon={<PhoneOutlined />}
                          href={`tel:${contactPhone.replace(/\s+/g, '')}`}
                          style={{ background: '#2563eb', borderRadius: 8, fontWeight: 600 }}
                        >
                          Call: {contactPhone}
                        </Button>
                      )}
                      {contactEmail && (
                        <Button
                          icon={<MailOutlined />}
                          href={`mailto:${contactEmail}`}
                          style={{ borderRadius: 8, fontWeight: 500 }}
                        >
                          Email Support
                        </Button>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Card>
            )}

        {/* Overview Stats Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ borderRadius: 12, borderLeft: '4px solid #1890ff' }}>
              <Statistic title="Total Support Tickets" value={totalCount} prefix={<MessageOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ borderRadius: 12, borderLeft: '4px solid #ff4d4f' }}>
              <Statistic title="Open Tickets" value={openCount} valueStyle={{ color: '#ff4d4f' }} prefix={<ClockCircleOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ borderRadius: 12, borderLeft: '4px solid #faad14' }}>
              <Statistic title="In Progress" value={inProgressCount} valueStyle={{ color: '#faad14' }} prefix={<SyncOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ borderRadius: 12, borderLeft: '4px solid #52c41a' }}>
              <Statistic title="Resolved Tickets" value={resolvedCount} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
            </Card>
          </Col>
        </Row>

        {/* Filter Controls Bar */}
        <Card style={{ marginBottom: 24, borderRadius: 12 }}>
          <Row gutter={[16, 16]} align="middle">
            {/* Search */}
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by name, email, or message..."
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>

            {/* Store Filter (SuperAdmin) */}
            {isSuperAdmin && (
              <Col xs={24} sm={12} md={6}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Filter by Store"
                  value={selectedStoreId}
                  onChange={(val) => {
                    setSelectedStoreId(val);
                    setPage(1);
                  }}
                >
                  <Option value="ALL">All Stores</Option>
                  {stores.map((s) => (
                    <Option key={s.id} value={String(s.id)}>
                      {s.name} ({s.shop})
                    </Option>
                  ))}
                </Select>
              </Col>
            )}

            {/* Status Filter */}
            <Col xs={24} sm={12} md={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by Status"
                value={selectedStatus}
                onChange={(val) => {
                  setSelectedStatus(val);
                  setPage(1);
                }}
              >
                <Option value="ALL">All Statuses</Option>
                <Option value="OPEN">OPEN</Option>
                <Option value="IN_PROGRESS">IN_PROGRESS</Option>
                <Option value="RESOLVED">RESOLVED</Option>
                <Option value="CLOSED">CLOSED</Option>
              </Select>
            </Col>

            {/* Action Buttons */}
            <Col xs={24} sm={12} md={4} style={{ textAlign: 'right' }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  Search
                </Button>
                <Button onClick={handleResetFilters}>Reset</Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Tickets Data Table */}
        <Card style={{ borderRadius: 12 }}>
          <Table
            columns={columns}
            dataSource={tickets}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize: limit,
              total: totalCount,
              onChange: (p, pSize) => {
                setPage(p);
                setLimit(pSize);
              },
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} support tickets`,
            }}
          />
        </Card>

        {/* Manage & Reply Ticket Modal */}
        <Modal
          title={
            <Space>
              <CustomerServiceOutlined style={{ color: '#0700d6' }} />
              <span>Manage Support Ticket #{selectedTicket?.id}</span>
            </Space>
          }
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          width={650}
          destroyOnHidden
        >
          {selectedTicket && (
            <div>
              {/* Ticket Meta Info */}
              <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                <Row gutter={[16, 12]}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Submitted By</Text>
                    <Text strong>{selectedTicket.name}</Text> ({selectedTicket.email})
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Store Name</Text>
                    <Text strong>{selectedTicket.store?.name || 'Default Store'}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Subject</Text>
                    <Text strong>{selectedTicket.subject || 'Support Request'}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Date Submitted</Text>
                    <Text>{moment(selectedTicket.created_at).format('DD MMM YYYY, hh:mm A')}</Text>
                  </Col>
                </Row>
              </div>

              {/* Original Message */}
              <div style={{ marginBottom: 20 }}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>Message Body:</Text>
                <div style={{ background: '#ffffff', padding: 12, borderRadius: 6, border: '1px solid #e5e7eb', whiteSpace: 'pre-wrap' }}>
                  {selectedTicket.message}
                </div>
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {/* Response & Status Form */}
              <Form form={form} layout="vertical" onFinish={handleSaveTicket}>
                <Form.Item
                  name="status"
                  label="Update Ticket Status"
                  rules={[{ required: true, message: 'Please select a status' }]}
                >
                  <Select>
                    <Option value="OPEN">
                      <Tag color="error">OPEN</Tag> Ticket is waiting for review
                    </Option>
                    <Option value="IN_PROGRESS">
                      <Tag color="processing">IN_PROGRESS</Tag> Under active investigation
                    </Option>
                    <Option value="RESOLVED">
                      <Tag color="success">RESOLVED</Tag> Support issue has been solved
                    </Option>
                    <Option value="CLOSED">
                      <Tag color="default">CLOSED</Tag> Ticket closed
                    </Option>
                  </Select>
                </Form.Item>

                <Form.Item name="reply" label="Admin Reply / Resolution Notes">
                  <TextArea
                    rows={4}
                    placeholder="Enter resolution notes or message to send back to the driver..."
                  />
                </Form.Item>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                  <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={submitting} icon={<CheckCircleOutlined />}>
                    Save Changes
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </Modal>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SupportTickets;
